import { PLAN_BULK_MAX_IDS, type StaffRole, type TargetedPlanDto } from '@planuze/admin-contracts';
import {
  Button,
  Checkbox,
  EmptyState,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@planuze/ui';
import { Ticket } from 'lucide-react';
import type { PlansContent } from '../../content/sections/plans.js';
import { usePlansContent } from '../../content/sections/plans.js';
import { formatCents } from '../../lib/currency.js';
import { fmtDate } from '../../lib/format.js';

type Tone = 'success' | 'neutral';
const MANAGE_ROLES: readonly StaffRole[] = ['super_admin', 'billing_ops'];

/* Plano direcionado expira pela janela `validUntil` (ADR-0435) — a row segue `active`,
   mas a UI mostra "Expirado" e o licensing já cai no tier público. Fonte única da regra:
   também alimenta o filtro de status da TargetedSection (ADR-0704). */
export function isTargetedPlanExpired(plan: TargetedPlanDto, now: Date): boolean {
  return plan.validUntil !== null && new Date(plan.validUntil).getTime() < now.getTime();
}

function statusOf(
  plan: TargetedPlanDto,
  now: Date,
  copy: PlansContent['targetedTable'],
): { tone: Tone; label: string } {
  return isTargetedPlanExpired(plan, now)
    ? { tone: 'neutral', label: copy.statusExpired }
    : { tone: 'success', label: copy.statusActive };
}

function targetLabel(
  plan: TargetedPlanDto,
  copy: PlansContent['targetedTable'],
): { kind: string; id: string } {
  if (plan.orgId !== null) return { kind: copy.targetOrg, id: plan.orgId };
  if (plan.userId !== null) return { kind: copy.targetUser, id: plan.userId };
  return { kind: copy.targetNone, id: '' };
}

interface Props {
  readonly plans: readonly TargetedPlanDto[];
  readonly staffRole: StaffRole;
  readonly onEdit: (plan: TargetedPlanDto) => void;
  readonly onArchive: (plan: TargetedPlanDto) => void;
  readonly selectedIds?: ReadonlySet<string>;
  readonly onToggle?: (id: string) => void;
  readonly onToggleAll?: (checked: boolean) => void;
  /** Há busca ativa — diferencia "sem resultados" de "sem planos" (ADR-0702). */
  readonly hasFilters: boolean;
  readonly onClearFilters: () => void;
}

/* Tabela de planos negociados sobre o primitive `Table` de @planuze/ui (ADR-0700);
   coluna de preço com tabular-nums. */
export function TargetedPlansTable({
  plans,
  staffRole,
  onEdit,
  onArchive,
  selectedIds,
  onToggle,
  onToggleAll,
  hasFilters,
  onClearFilters,
}: Props) {
  const copy = usePlansContent();
  const canManage = MANAGE_ROLES.includes(staffRole);
  const now = new Date();
  if (plans.length === 0) {
    return hasFilters ? (
      <EmptyState
        icon={<Ticket className="size-6" />}
        title={copy.targetedTable.emptyFilteredTitle}
        description={copy.targetedTable.emptyFilteredDescription}
        primaryAction={
          <Button variant="outline" onClick={onClearFilters}>
            {copy.common.clearFilters}
          </Button>
        }
      />
    ) : (
      <EmptyState icon={<Ticket className="size-6" />} title={copy.targetedTable.emptyTitle} />
    );
  }
  const selection = selectedIds ?? new Set<string>();
  const bulkEnabled = canManage && selectedIds !== undefined;
  const selectedVisibleCount = plans.filter((plan) => selection.has(plan.id)).length;
  const selectionTarget = Math.min(plans.length, PLAN_BULK_MAX_IDS);
  const allSelected = selectionTarget > 0 && selectedVisibleCount >= selectionTarget;
  const selectionFull = selectedVisibleCount >= PLAN_BULK_MAX_IDS;
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {bulkEnabled && (
            <TableHead className="w-10">
              <Checkbox
                checked={allSelected}
                onCheckedChange={(checked) => onToggleAll?.(checked === true)}
                aria-label={copy.targetedTable.selectPageAriaLabel}
              />
            </TableHead>
          )}
          <TableHead>{copy.targetedTable.columnPlan}</TableHead>
          <TableHead>{copy.targetedTable.columnTier}</TableHead>
          <TableHead>{copy.targetedTable.columnTarget}</TableHead>
          <TableHead>{copy.targetedTable.columnPrice}</TableHead>
          <TableHead>{copy.targetedTable.columnValidity}</TableHead>
          <TableHead>{copy.targetedTable.columnStatus}</TableHead>
          {canManage && <TableHead />}
        </TableRow>
      </TableHeader>
      <TableBody>
        {plans.map((plan) => {
          const status = statusOf(plan, now, copy.targetedTable);
          const target = targetLabel(plan, copy.targetedTable);
          return (
            <TableRow key={plan.id}>
              {bulkEnabled && (
                <TableCell>
                  <Checkbox
                    checked={selection.has(plan.id)}
                    disabled={selectionFull && !selection.has(plan.id)}
                    onCheckedChange={() => onToggle?.(plan.id)}
                    aria-label={copy.targetedTable.selectRowAriaLabel(plan.displayName)}
                  />
                </TableCell>
              )}
              <TableCell className="text-foreground">
                {plan.displayName}
                <span className="ml-2 text-xs text-muted-foreground">{plan.lookupKey}</span>
              </TableCell>
              <TableCell className="text-muted-foreground">{plan.tier}</TableCell>
              <TableCell className="text-muted-foreground">
                {target.kind} <code className="text-xs">{target.id.slice(0, 10)}</code>
              </TableCell>
              <TableCell numeric className="text-muted-foreground">
                {formatCents(plan.priceUsdCents, plan.currency)}
              </TableCell>
              <TableCell className="text-muted-foreground">
                {plan.validFrom === null ? '∞' : fmtDate(plan.validFrom)} →{' '}
                {plan.validUntil === null ? '∞' : fmtDate(plan.validUntil)}
              </TableCell>
              <TableCell>
                <StatusBadge variant={status.tone}>{status.label}</StatusBadge>
              </TableCell>
              {canManage && (
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={copy.targetedTable.editAriaLabel(plan.displayName)}
                      onClick={() => onEdit(plan)}
                    >
                      {copy.targetedTable.edit}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={copy.targetedTable.archiveAriaLabel(plan.displayName)}
                      onClick={() => onArchive(plan)}
                    >
                      {copy.common.archive}
                    </Button>
                  </div>
                </TableCell>
              )}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
